import { useEffect, useState } from 'react';
import { formatDate, formatMoney, type FinanceInvoice } from '@/lib/finance';
import {
  PAYMENT_METHODS_SHORT,
  fetchInvoicePayments,
  type InvoicePayment,
} from '@/lib/invoicePayments';
import { useFinance } from '@/hooks/useFinance';
import { inputClass } from '@/pages/dashboard/finance/components/FinanceUI';

/**
 * Modale d'encaissement : saisie d'un paiement partiel (montant + date),
 * avec l'historique réel de tous les encaissements de la facture.
 */
export default function InvoicePaymentModal({
  invoice,
  currency,
  onClose,
  onDone,
}: {
  invoice: FinanceInvoice;
  currency: string;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const finance = useFinance();
  const cur = invoice.currency || currency;
  const total = Number(invoice.total) || 0;
  const paid = Number(invoice.amount_paid) || 0;
  const remaining = Math.max(0, total - paid);

  const [amount, setAmount] = useState<string>(remaining > 0 ? String(Math.round(remaining * 100) / 100) : '');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('Espèces');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<InvoicePayment[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await fetchInvoicePayments(invoice.id);
        if (alive) setHistory(rows);
      } catch {
        if (alive) setHistory([]);
      } finally {
        if (alive) setLoadingHistory(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [invoice.id]);

  const submit = async () => {
    setError(null);
    const value = Number(String(amount).replace(',', '.'));
    if (!Number.isFinite(value) || value <= 0) {
      setError('Saisissez un montant valide supérieur à 0.');
      return;
    }
    if (value > remaining + 0.009) {
      setError(`Le montant dépasse le reste à payer (${formatMoney(remaining, cur)}).`);
      return;
    }
    if (!paidAt) {
      setError('La date du paiement est obligatoire.');
      return;
    }
    setBusy(true);
    try {
      const result = await finance.recordInvoicePayment(invoice, value, paidAt, method, note.trim() || null);
      onDone(
        result.status === 'paid'
          ? `Facture ${invoice.number} entièrement soldée (${formatMoney(value, cur)} encaissés).`
          : `${formatMoney(value, cur)} encaissés — reste ${formatMoney(Math.max(0, total - result.amountPaid), cur)}.`,
      );
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible d'enregistrer ce paiement.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] flex flex-col mx-0 sm:mx-4">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200/70 flex-shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
              <i className="ri-hand-coin-line text-primary-500"></i>
              Enregistrer un paiement
            </h3>
            <p className="text-xs text-foreground-500 mt-0.5 truncate">
              {invoice.number} · {invoice.customer_name || 'Client'}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="rounded-lg bg-background-100 p-2.5">
              <p className="text-[11px] text-foreground-500">Total</p>
              <p className="text-xs font-bold text-foreground-950 truncate">{formatMoney(total, cur)}</p>
            </div>
            <div className="rounded-lg bg-primary-50 p-2.5">
              <p className="text-[11px] text-primary-700">Déjà payé</p>
              <p className="text-xs font-bold text-primary-800 truncate">{formatMoney(paid, cur)}</p>
            </div>
            <div className="rounded-lg bg-accent-50 p-2.5">
              <p className="text-[11px] text-accent-700">Reste</p>
              <p className="text-xs font-bold text-accent-800 truncate">{formatMoney(remaining, cur)}</p>
            </div>
          </div>

          {remaining <= 0 ? (
            <div className="flex items-center gap-2 px-3.5 py-3 rounded-lg bg-primary-50 text-primary-800 text-sm mb-4">
              <i className="ri-checkbox-circle-line"></i>
              Cette facture est entièrement payée.
            </div>
          ) : (
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Montant encaissé *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className={inputClass}
                  placeholder="0.00"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Date du paiement *</label>
                  <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Mode</label>
                  <select value={method} onChange={(e) => setMethod(e.target.value)} className={`${inputClass} cursor-pointer`}>
                    {PAYMENT_METHODS_SHORT.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Note (facultatif)</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} className={inputClass} placeholder="Ex. acompte, chèque n°…" maxLength={200} />
              </div>
              {error && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-xs">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  <span>{error}</span>
                </div>
              )}
            </div>
          )}

          <div>
            <p className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
              <i className="ri-history-line text-foreground-400"></i>
              Encaissements enregistrés
            </p>
            {loadingHistory ? (
              <div className="flex items-center gap-2 py-3 text-xs text-foreground-400">
                <i className="ri-loader-4-line animate-spin"></i>Chargement…
              </div>
            ) : history.length === 0 ? (
              <p className="text-xs text-foreground-400 py-2">Aucun encaissement détaillé pour le moment.</p>
            ) : (
              <div className="divide-y divide-background-200/70 border border-background-200/70 rounded-lg overflow-hidden">
                {history.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 px-3 py-2">
                    <span className="w-7 h-7 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-foreground-900">{formatMoney(Number(p.amount), p.currency || cur)}</p>
                      <p className="text-[11px] text-foreground-400 truncate">
                        {formatDate(p.paid_at)}{p.method ? ` · ${p.method}` : ''}{p.note ? ` · ${p.note}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-background-200/70 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full border border-background-200/70 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
          >
            Fermer
          </button>
          {remaining > 0 && (
            <button
              onClick={submit}
              disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              Enregistrer le paiement
            </button>
          )}
        </div>
      </div>
    </div>
  );
}